import type { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, input, output, inject } from '@angular/core';

import { AnalyticsMode } from '@core/analytics-types';
import { CandidateAnalytics } from '@core/candidate-util';
import type { DisqualifyReason } from '@core/disqualify-reasons-types';
import { DisqualifyReasonsDropdownComponent } from '@core/disqualify-reasons-ui';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { Placement } from '@core/placements-types';
import { RequisitionOpeningIssue } from '@core/requisitions-types';
import type { Stage } from '@core/stages-types';
import { StageGroup } from '@core/stages-types';
import { ButtonComponent, IconComponent, RtTooltip } from '@core/user-interface-legacy';
import { WalkthroughStepOriginDirective } from '@core/walkthrough-ui';

import type {
  DisqualifyEvent,
  PlacementActions,
  PlacementEvent,
  ProceedEvent,
} from '../../candidate-profile-placements/types';
import { getNextStage } from '../../candidate-profile-placements/utils';

@Component({
  selector: 'rt-action-buttons',
  templateUrl: './action-buttons.component.html',
  styleUrls: ['./action-buttons.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    TranslatePipe,
    RtTooltip,
    WalkthroughStepOriginDirective,
    DisqualifyReasonsDropdownComponent,
  ],
})
export class ActionButtonsComponent {
  private readonly i18n = inject(I18nService);
  private readonly candidateAnalytics = inject(CandidateAnalytics);

  public readonly RequisitionOpeningIssue = RequisitionOpeningIssue;

  public readonly disqualifyDropdownPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  public placement = input.required<Placement>();

  public placementActions = input.required<PlacementActions>();

  public stages = input.required<Stage[]>();

  public disqualifyReasons = input.required<DisqualifyReason[]>();

  public isLoading = input<boolean>(false);

  public openingIssue = input<RequisitionOpeningIssue>();

  public isLastStage = computed(() => !this.nextStage());

  public showProceedAndDisqualifyButtons = computed(() => {
    if (this.placement().hiredInThisPlacement && this.isLastStage()) {
      return false;
    }

    return this.placementActions().disqualify || this.placementActions().proceed;
  });

  public nextStage = computed(() => getNextStage(this.placement(), this.stages()));

  public isProceedButtonDisabled = computed(() => {
    if (this.isLoading()) {
      return true;
    }

    if (!this.nextStage()) {
      return true;
    }

    return this.nextStage()!.group === StageGroup.Hires && !this.placementActions().hire;
  });

  public proceedButtonTooltip = computed(() => {
    if (!this.nextStage()) {
      return '';
    }

    if (this.nextStage()!.group === StageGroup.Hires && !this.placementActions().hire) {
      return this.i18n.translate('app.stages.no_hires_permission');
    }

    if (this.placementActions().proceed) {
      return this.i18n.translate('app.candidate.move_to') + ' ' + this.nextStage()?.name;
    }

    return '';
  });

  public proceedButtonTooltipPosition = computed(() =>
    this.isNextStageHired() ? 'below' : 'above',
  );

  public isNextStageHired = computed(
    () =>
      this.placement().stage.group !== StageGroup.Hires &&
      this.nextStage()?.group === StageGroup.Hires,
  );

  public proceedClick = output<ProceedEvent>();

  public editHiringDetailsClick = output<PlacementEvent>();

  public requalifyClick = output<PlacementEvent>();

  public disqualifyReasonSelect = output<DisqualifyEvent>();

  public editDisqualifyReasonsClick = output<void>();

  public disqualifyReasonsActionsDetails = output<DisqualifyReason>();

  public onProceedClick($event: MouseEvent): void {
    if (this.isProceedButtonDisabled()) {
      $event.stopImmediatePropagation();
      return;
    }

    const nextStage = this.nextStage();

    if (!nextStage) {
      return;
    }

    this.proceedClick.emit({
      placement: this.placement(),
      targetStage: nextStage,
      originStage: this.placement().stage,
      eventOrigin: 'proceed-click',
    });
  }

  public disqualifyReasonClick(disqualifyReason: DisqualifyReason): void {
    this.candidateAnalytics.trackDisqualify(AnalyticsMode.Single);
    this.disqualifyReasonSelect.emit({ placement: this.placement(), disqualifyReason });
  }

  public onRequalifyClick(): void {
    this.candidateAnalytics.trackRequalify(AnalyticsMode.Single);
    this.requalifyClick.emit({ placement: this.placement() });
  }
}
